// local
import {
  applyEach,
  findElements,
  isCrawler,
  supportsIntersectionObserver,
  supportsRequestIdleCallback,
} from './utils.js';

// constants
const QUERY = '[data-background-image]';
const win = window;

/**
 * Flips any data-background-image attributes to style: background-image.
 *
 * @param {HTMLElement} element
 * @returns {void}
 */
function load(element) {
  const image = element.getAttribute('data-background-image');
  element.style.backgroundImage = `url(${image})`;
  element.removeAttribute('data-background-image');
}

/**
 *
 * @param {Object} params
 * @param {number} [params.verticalLoadingTriggerHeight] - The distance in pixels above or below an element that should considered the trigger area for loading an image. For example - if an image has a height of 400px then the area 200px above or below it will activate a load once in the viewport.
 * @returns {void}
 */
export function init({ verticalLoadingTriggerHeight = 200 } = {}) {
  /**
   * We do the initial ask so we can act on (and update) this later.
   * @type {HTMLElement[]}
   */
  let elements = findElements(QUERY);

  if (!supportsIntersectionObserver || isCrawler) {
    // we're in a bot browser or browser without IntersectionObserver, just load them
    applyEach(elements, load);
  } else {
    // we're in a non-bot browser with IntersectionObserver
    const observer = new win.IntersectionObserver(
      (entries) => {
        applyEach(entries, (entry) => {
          if (entry.isIntersecting || entry.intersectionRatio) {
            const element = /** @type {HTMLElement} */ (entry.target);

            if (supportsRequestIdleCallback) {
              // @ts-ignore: stops Ryan's editor from yelling at him
              win.requestIdleCallback(
                () => {
                  load(element);
                },
                {
                  timeout: 200,
                }
              );
            } else {
              load(element);
            }

            observer.unobserve(element);
            elements = elements.filter((el) => el !== element);

            // if we've activated all available options, we're done here
            if (!elements.length) {
              observer.disconnect();
            }
          }
        });
      },
      { rootMargin: `${verticalLoadingTriggerHeight}px 0%` }
    );

    applyEach(elements, (element) => {
      observer.observe(element);
    });
  }
}
